import { Injectable } from '@angular/core';

/**
 * Correct MIME types for the export formats this app produces. Servers sometimes
 * return generated files as a generic type (e.g. text/html), which causes iOS
 * Safari to render the raw file bytes instead of downloading them. Re-typing the
 * blob before download guarantees the browser and downstream apps (Excel,
 * Numbers, Google Sheets) recognise it as a real file.
 */
const MIME_TYPES_BY_EXTENSION: { [extension: string]: string } = {
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  xls: 'application/vnd.ms-excel',
  csv: 'text/csv',
  pdf: 'application/pdf'
};

@Injectable({
  providedIn: 'root'
})
export class BlobDownloadService {
  downloadDataUrl(dataUrl: string, filename: string): boolean {
    const match = /^data:([^;,]+);base64,([A-Za-z0-9+/=\s]+)$/.exec(dataUrl || '');
    if (!match) return false;
    try {
      const bytes = Uint8Array.from(atob(match[2]), char => char.charCodeAt(0));
      this.downloadBlob(new Blob([bytes], {type: match[1]}), filename.split('/').pop() || 'attachment');
      return true;
    } catch { return false; }
  }

  downloadUrl(url: string, filename: string): void {
    if (new URL(url).protocol !== 'https:') {
      throw new Error('The download link is invalid.');
    }
    // Storage sends Content-Disposition: attachment. The browser handles the
    // transfer directly without buffering the whole file in Angular memory.
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.rel = 'noopener noreferrer';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  }

  downloadBlob(blob: Blob, filename: string): void {
    const typedBlob = this.withCorrectMimeType(blob, filename);
    const url = URL.createObjectURL(typedBlob);
    const anchor = document.createElement('a');

    anchor.href = url;
    anchor.rel = 'noopener noreferrer';
    // Always force a download. This service used to open exports in a new tab on
    // iOS/Safari because older Safari ignored the `download` attribute — but that
    // made the browser render the raw .xlsx bytes (the "PK... [Content_Types].xml"
    // screen users reported). Modern Safari (13+) honours `download`, so setting it
    // unconditionally downloads a valid file on every browser, desktop or handheld.
    anchor.download = filename;

    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);

    // Defer revocation so Safari has time to begin the download before the object
    // URL is invalidated; revoking synchronously can cancel the download on iOS.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  private withCorrectMimeType(blob: Blob, filename: string): Blob {
    const extension = filename.split('.').pop()?.toLowerCase() ?? '';
    const expectedType = MIME_TYPES_BY_EXTENSION[extension];

    if (expectedType && blob.type !== expectedType) {
      return new Blob([blob], { type: expectedType });
    }

    return blob;
  }
}
